import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import Logo from "./brand/Logo";

const NAV = [
  { to: "/guide", label: "Guide" },
  { to: "/categorie", label: "Categorie" },
  { to: "/liste", label: "Liste" },
  { to: "/about", label: "About" },
];

const MOBILE_NAV_ID = "mobile-nav";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-line" : "bg-white border-b border-transparent"
      }`}
    >
      <div className="page-container h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-link"
          className="inline-flex items-center"
          aria-label="Glowkit — Home"
        >
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Principale">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `label-eyebrow link-underline ${isActive ? "opacity-100" : "opacity-70 hover:opacity-100"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <span className="label-eyebrow opacity-60">Selezioni curate</span>
          <Link to="/liste/best-of-under-30" data-testid="header-cta" className="btn-ink">
            Best under €30
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden min-h-11 min-w-11 inline-flex items-center justify-center -mr-1"
          aria-label={open ? "Chiudi menu" : "Apri menu"}
          aria-expanded={open}
          aria-controls={MOBILE_NAV_ID}
          data-testid="menu-toggle"
        >
          {open ? <X size={20} strokeWidth={1.4} /> : <Menu size={20} strokeWidth={1.4} />}
        </button>
      </div>

      {/* Mobile drawer — inert when closed so focus cannot land inside max-h-0 panel */}
      <div
        id={MOBILE_NAV_ID}
        className={`md:hidden overflow-hidden bg-white transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[80vh] opacity-100 border-t border-line" : "max-h-0 opacity-0"
        }`}
        aria-hidden={!open}
        inert={!open ? true : undefined}
      >
        <nav className="page-container py-8 flex flex-col gap-6" aria-label="Menu mobile">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              tabIndex={open ? undefined : -1}
              data-testid={`nav-mobile-${n.label.toLowerCase()}`}
              className="serif text-3xl leading-none py-1"
            >
              {n.label}
            </NavLink>
          ))}
          <div className="hairline mt-4 pt-6">
            <Link
              to="/liste/best-of-under-30"
              tabIndex={open ? undefined : -1}
              data-testid="mobile-cta"
              className="btn-ink w-full justify-center"
            >
              Best under €30
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
}
